import { useAggregateTrustScore } from '../../../../../hooks/useDataQuality'
import type { TimeWindow } from '../../../../../types/dataQuality'
import LoadingSkeleton from '../../../../common/LoadingSkeleton'
import ErrorDisplay from '../../../../common/ErrorDisplay'
import './TrustScoreBreakdown.css'

interface TrustScoreBreakdownProps {
  timeWindow: TimeWindow
}

const TrustScoreBreakdown = ({ timeWindow }: TrustScoreBreakdownProps) => {
  const { data, isLoading, error } = useAggregateTrustScore({ time_window: timeWindow })

  if (isLoading) {
    return (
      <div className="data-trust-section">
        <div className="section-header-compact">
          <h3 className="section-title-compact">Data Trust Score</h3>
        </div>
        <LoadingSkeleton variant="chart" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="data-trust-section">
        <ErrorDisplay
          error={error.message}
          onRetry={() => window.location.reload()}
          title="Failed to Load Trust Score"
        />
      </div>
    )
  }

  if (!data || !data.kpi_breakdown) {
    return (
      <div className="data-trust-section">
        <div className="section-header-compact">
          <h3 className="section-title-compact">Data Trust Score</h3>
          <p className="section-subtitle-compact">Composite reliability index (0-100)</p>
        </div>
        <p className="no-data-message">No trust score data available</p>
      </div>
    )
  }

  const trustScore = Math.round(data.overall_trust_score)

  // Define weights according to the integration guide
  const weights = {
    completeness_score: 0.25,
    freshness_score: 0.25,
    quality_rules_score: 0.25,
    schema_stability_score: 0.10,
    pipeline_success_score: 0.10,
  }

  const formatMetricName = (key: string): string => {
    const nameMap: { [key: string]: string } = {
      completeness_score: 'Completeness',
      freshness_score: 'Timeliness',
      quality_rules_score: 'Quality Rules',
      schema_stability_score: 'Schema Stability',
      pipeline_success_score: 'Error Rate Inverse',
    }
    return nameMap[key] || key
  }

  // Calculate weighted components
  const components = Object.entries(data.kpi_breakdown)
    .filter(([key]) => key in weights)
    .map(([key, metric]) => {
      const weight = weights[key as keyof typeof weights] || 0
      const weightedValue = (metric.value * weight)

      return {
        name: formatMetricName(key),
        weight: (weight * 100).toFixed(0),
        weightedValue: weightedValue.toFixed(1),
        rawValue: metric.value.toFixed(1),
      }
    })

  // Calculate gauge percentage (95% of circle for visual)
  const gaugePercentage = (trustScore / 100) * 95

  // Determine color based on score
  // >= 90 = green (#00C950)
  // >= 80 = yellow (#EFB100)
  // < 80 = red (#FB2C36)
  const getScoreColor = (score: number): string => {
    if (score >= 90) return '#00C950' // Green - Healthy
    if (score >= 80) return '#EFB100' // Yellow - Monitor
    return '#FB2C36' // Red - Action needed
  }

  const scoreColor = getScoreColor(trustScore)

  return (
    <div className="data-trust-section">
      <div className="section-header-compact">
        <h3 className="section-title-compact">Data Trust Score</h3>
        <p className="section-subtitle-compact">Composite reliability index (0-100)</p>
      </div>

      <div className="trust-score-container">
        <div className="trust-circle-wrapper">
          <svg className="trust-circle" width="200" height="200">
            {/* Background arc */}
            <circle
              cx="100"
              cy="100"
              r="80"
              fill="none"
              stroke="#f3f4f6"
              strokeWidth="16"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 80 * 0.95} ${2 * Math.PI * 80 * 0.05}`}
              transform="rotate(-90 100 100)"
            />
            {/* Progress arc - dynamic color based on score */}
            <circle
              cx="100"
              cy="100"
              r="80"
              fill="none"
              stroke={scoreColor}
              strokeWidth="16"
              strokeLinecap="round"
              strokeDasharray={`${(gaugePercentage / 100) * 2 * Math.PI * 80} ${2 * Math.PI * 80}`}
              transform="rotate(-90 100 100)"
              className="trust-progress-arc"
            />
          </svg>

          <div className="trust-center-value">
            <div className="trust-score-large">{trustScore}</div>
            <div className="trust-score-total">/ 100</div>
          </div>
        </div>

        <div className="trust-right-content">
          <div className="trust-badge-row">
            <div
              className="trust-status-badge"
              style={{
                backgroundColor: trustScore >= 90 
                  ? 'rgba(0, 201, 80, 0.1)' 
                  : trustScore >= 80 
                  ? 'rgba(239, 177, 0, 0.1)' 
                  : 'rgba(251, 44, 54, 0.1)',
                color: scoreColor,
                borderColor: scoreColor,
                border: `1px solid ${scoreColor}`
              }}
            >
              {trustScore >= 90 ? 'EXCELLENT' : trustScore >= 80 ? 'GOOD' : 'NEEDS IMPROVEMENT'}
            </div>
          </div>

          <div className="trust-breakdown-list">
            <h4 className="trust-breakdown-title">Component Breakdown</h4>
            {components.map((component, index) => (
              <div key={index} className="trust-breakdown-item">
                <span className="trust-breakdown-label">
                  {component.name} ({component.weight}%)
                </span>
                <span className="trust-breakdown-value">{component.weightedValue}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* <div className="trust-footer-metrics">
        <div className="trust-footer-item">
          <span className="trust-footer-label">Total Sources</span>
          <span className="trust-footer-value">{data.total_sources || 0}</span>
        </div>
        <div className="trust-footer-item">
          <span className="trust-footer-label">Above Threshold</span>
          <span className="trust-footer-value">{data.sources_above_threshold || 0}</span>
        </div>
      </div> */}
    </div>
  )
}

export default TrustScoreBreakdown
